import { Link } from 'react-router-dom';
import { FileText, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { WriterProjectOverview } from '@/hooks/useWriterProjectsOverview';
import { useMyPapers } from '@/hooks/useMyPapers';
import { draftStatusChip, relativeTime } from './draftStatus';

interface DraftRailProps {
  projects: WriterProjectOverview[];
  loading?: boolean;
  onNewDraft?: () => void;
}

/**
 * Every active draft, dense and quiet. Published papers appear underneath as
 * the outcome of writing, not as a parallel kingdom.
 */
const DraftRail = ({ projects, loading, onNewDraft }: DraftRailProps) => {
  const { publicPapers } = useMyPapers();

  return (
    <section className="mb-12">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Your drafts</h2>
        {projects.length > 0 && (
          <span className="text-xs text-muted-foreground">{projects.length} active</span>
        )}
      </div>

      {loading ? (
        <div className="rounded-xl border border-border/60 bg-card p-6 text-sm text-muted-foreground">
          Loading drafts…
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border/60 bg-card/50 p-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            No drafts yet. Start one and describe the paper you want to write.
          </p>
          <Button size="sm" variant="outline" onClick={onNewDraft}>
            Start a draft
          </Button>
        </div>
      ) : (
        <ul className="rounded-xl border border-border/60 bg-card divide-y divide-border/60">
          {projects.map((p) => {
            const chip = draftStatusChip(p.compileStatus);
            return (
              <li key={p.id}>
                <Link
                  to={`/write/${p.id}`}
                  className="flex items-center gap-4 px-5 py-4 transition-colors hover:bg-accent/40"
                >
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {p.title || 'Untitled draft'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Edited {relativeTime(p.updatedAt)}
                      {p.versionCount > 0 && ` · ${p.versionCount} version${p.versionCount === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px]',
                      chip.tone === 'attention'
                        ? 'border-destructive/30 text-destructive'
                        : 'border-border/60 text-muted-foreground',
                    )}
                  >
                    {chip.live && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
                    )}
                    {chip.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {publicPapers.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
            Published
          </h3>
          <ul className="rounded-xl border border-border/60 bg-card divide-y divide-border/60">
            {publicPapers.slice(0, 5).map((paper) => (
              <li key={paper.id}>
                <Link
                  to={`/paper/${paper.id}`}
                  className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:bg-accent/40"
                >
                  <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                    {paper.title}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {paper.views ?? 0} views
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

export default DraftRail;
